import React, { useEffect, useState } from "react";
import "./header.css";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Link, useLocation } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import {
  getRefreshToken,
  getTokenID,
  isEmployerLogin,
  permissionStatus,
  role,
} from "../redux/modules/userSlice";
import { useDispatch, useSelector } from "react-redux";

const getRandomColor = () => {
  const getRandomHexDigit = () => Math.floor(Math.random() * 16).toString(16);
  return `#${getRandomHexDigit()}${getRandomHexDigit()}${getRandomHexDigit()}${getRandomHexDigit()}${getRandomHexDigit()}${getRandomHexDigit()}`;
};

const Header = () => {
  const isEmployer = useSelector((state) => state.user.isEmployer);
  const roles = useSelector((state) => state.user.role);
  const storedColor = localStorage.getItem("userColor");
  const initialRandomColor = storedColor || getRandomColor();
  const [randomColor, setRandomColor] = useState(initialRandomColor);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!storedColor) {
      localStorage.setItem("userColor", randomColor);
    }
  }, [randomColor, storedColor]);

  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const handleLogout = () => {
    localStorage.clear();
    dispatch(getTokenID(null));
    dispatch(getRefreshToken(null));
    dispatch(permissionStatus(null));
    dispatch(isEmployerLogin(false));
    dispatch(role(null));
    const d = new Date();
    const expires = "expires=" + d.toUTCString();
    document.cookie = "_secure_ARJ_=;" + expires + ";path=/";
    document.cookie = "_secure_ref=;" + expires + ";path=/";
    document.cookie = "cookiesAccepted=;" + expires + ";path=/";
    handleCloseUserMenu();
    window.location.href = "/login";
    sessionStorage.clear();
  };

  const email = localStorage.getItem("email_id");
  const username = localStorage.getItem("name");

  return (
    <AppBar color="inherit" position="static" elevation={0}>
      <Container maxWidth="xxl" sx={{ padding: "12px 16px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography
            sx={{ color: "#151D48", fontSize: "24px", fontWeight: "500" }}
          />

          {/* ── Avatar trigger button ── */}
          <Box sx={{ flexGrow: 0 }}>
            <Box
              onClick={handleOpenUserMenu}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "8px",
                border: "1px solid #F1F2F3",
                background: "#FFF",
                py: "4px",
                pl: "4px",
                pr: "8px",
                cursor: "pointer",
                "&:hover": { background: "#f8fafc" },
              }}
            >
              <Avatar
                alt={email}
                src="/static/images/avatar/2.jpg"
                variant="rounded"
                sx={{ bgcolor: randomColor, width: 32, height: 32 }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  sx={{
                    color: "#151D48",
                    fontSize: "14px",
                    fontWeight: "500",
                    fontFamily: "Roboto",
                    mx: 1,
                  }}
                >
                  {username}
                </Typography>
                <Typography
                  sx={{
                    color: "#6A7885",
                    fontSize: "8px",
                    fontWeight: "400",
                    mx: 1,
                  }}
                >
                  {roles}
                </Typography>
              </Box>
              <IconButton
                sx={{
                  cursor: "pointer",
                  borderRadius: "4px",
                  p: "0",
                  background:
                    "linear-gradient(0deg, rgba(63,149,234,0.08) 0%, rgba(63,149,234,0.08) 100%), #FFF",
                }}
              >
                <KeyboardArrowDownIcon sx={{ color: "#3F95EA" }} />
              </IconButton>
            </Box>

            {/* ── Profile Popover ── */}
            <Popover
              open={Boolean(anchorElUser)}
              anchorEl={anchorElUser}
              onClose={handleCloseUserMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: "16px",
                  width: 240,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
                  overflow: "hidden",
                  p: 0,
                },
              }}
            >
              {/* Top section — light blue bg covers only top half of avatar */}
              <Box sx={{ position: "relative", background: "#fff" }}>
                {/* Blue bg strip — exactly half the avatar height (36px) */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 36 + 12, // half of 72px avatar + 12px top padding
                    background: "#e8f1fb",
                    zIndex: 0,
                  }}
                />

                {/* Avatar — sitting on the blue/white boundary */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    px: "12px",
                    pt: "12px",
                    pb: 0,
                  }}
                >
                  <Avatar
                    alt={username}
                    src="/static/images/avatar/2.jpg"
                    variant="rounded"
                    sx={{
                      bgcolor: randomColor,
                      width: 72,
                      height: 72,
                      borderRadius: "12px",
                      fontSize: "28px",
                    }}
                  />
                </Box>

                {/* Name / email / role */}
                <Box sx={{ px: "12px", pt: 1.5, pb: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "#0f1c2e",
                      lineHeight: 1.3,
                    }}
                  >
                    {username}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#6b7a8d",
                      mt: 0.3,
                    }}
                  >
                    {email}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 400,
                      color: "#9aa5b4",
                      mt: 0.3,
                    }}
                  >
                    {roles}
                  </Typography>
                </Box>

                {/* Divider with 8px gap above and below */}
                <Box sx={{ px: "12px", mt: "8px", mb: "8px" }}>
                  <Divider />
                </Box>

                {/* Change Password */}
                <Box sx={{ px: "12px", pb: "4px" }}>
                  <Box
                    component={Link}
                    to="/changepassword"
                    onClick={handleCloseUserMenu}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: "8px",
                      borderRadius: "8px",
                      textDecoration: "none",
                      color: "#1a2e4a",
                      "&:hover": { background: "#f4f6f8" },
                      px: 1,
                    }}
                  >
                    <LockOpenIcon sx={{ fontSize: "20px", color: "#444" }} />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#1a2e4a",
                      }}
                    >
                      Change Password
                    </Typography>
                  </Box>

                  {/* Logout */}
                  <Box
                    onClick={handleLogout}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: "8px",
                      px: 1,
                      mb: "4px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "&:hover": { background: "#fff5f5" },
                    }}
                  >
                    <img src="/assets/logout.svg" alt="" />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#e53935",
                      }}
                    >
                      Logout
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Popover>
          </Box>
        </Box>
      </Container>
    </AppBar>
  );
};

export default Header;
